import { User } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useCreatePost } from '@/hooks/useCreatePost';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import BeltIcon from '@/assets/images/icons/belt.png';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Whole Community' },
  { value: 'connections', label: 'Only my connections' },
  { value: 'gym', label: 'Only my gym' },
];

function VisibilitySelect({ value, onChange }: { value: string, onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const visibilityOptions = [
    { value: 'public', label: t('social.whole_community') },
    { value: 'connections', label: t('social.my_connections') },
  ];
  return (
    <div className="flex items-center">
      <img src={BeltIcon} alt="Belt" className="w-6 h-3 m-0" />
      <Select value={value} onValueChange={onChange} open={open} onOpenChange={setOpen}>
        <SelectTrigger className="h-10 bg-transparent text-white text-sm font-normal focus:outline-none border-0 !border-none shadow-none ring-0 focus:ring-0 focus:border-0 !ring-0 !outline-none min-w-[140px] pr-4 flex items-center [&_[data-radix-select-icon]]:hidden" style={{border:'none',boxShadow:'none',outline:'none'}}>
          <SelectValue />
          {open ? (
            <ChevronUp className="w-4 h-4 text-white ml-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white ml-0" />
          )}
        </SelectTrigger>
        <SelectContent className="bg-black border-0 py-2 space-y-1">
          {VISIBILITY_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value} className="text-white text-sm truncate">{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export const CreatePost = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const mutation = useCreatePost();
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const blurTimeout = useRef<NodeJS.Timeout | null>(null);
  const { t } = useI18n();

  // Só fecha se o foco sair de todo o container
  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    blurTimeout.current = setTimeout(() => {
      if (
        inputContainerRef.current &&
        !inputContainerRef.current.contains(document.activeElement)
      ) {
        setIsExpanded(false);
      }
    }, 0);
  };
  const handleContainerFocus = () => {
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
    }
  };

  const handleInputFocus = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setIsExpanded(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    await mutation.mutateAsync({ content, visibility });
    setContent('');
    setIsExpanded(false);
  };

  return (
    <>
      <div className="w-full max-w-2xl mx-auto px-1 bg-transparent mb-4">
        <form onSubmit={handleSubmit}>
          <div
            ref={inputContainerRef}
            className="flex items-end w-full gap-4 relative"
            tabIndex={-1}
            onBlur={handleContainerBlur}
            onFocus={handleContainerFocus}
          >
            {isExpanded ? (
              <div className="max-w-[240px] w-full min-h-[72px]">
                <div className="relative w-full bg-[#362B2B] rounded-2xl overflow-hidden">
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={t('social.share_something')}
                    className="w-full bg-transparent border-0 text-white placeholder-white/80 text-sm font-light px-5 py-2 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 resize-none box-border comment-selection"
                    maxLength={300}
                    rows={5}
                    readOnly={!user}
                    style={{ minHeight: '60px', maxHeight: '60px' }}
                  />
                  <div className="w-full px-4 py-1 flex justify-end">
                    <span className="text-xs text-white/60 pointer-events-none">
                      {content.length}/300
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-[240px] w-full">
                <div className="relative w-full bg-[#362B2B] rounded-2xl overflow-hidden">
                <input
                  type="text"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={t('social.share_something')}
                    className="w-full bg-transparent border-0 text-white placeholder-white/80 text-sm font-light px-5 py-2 h-8 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 box-border"
                  maxLength={300}
                  onFocus={handleInputFocus}
                  readOnly={!user}
                  />
                  <div className="w-full px-4 py-1 flex justify-end">
                    <span className="text-xs text-white/60 pointer-events-none">
                      {content.length}/300
                    </span>
                  </div>
              </div>
              </div>
            )}
            <Button
              type="submit"
              disabled={!content.trim() || mutation.isPending}
              className="shrink-0 rounded-full h-10 px-4 bg-sossiety-red-600 hover:bg-sossiety-red-700 text-white text-xs font-bold shadow-none flex items-center justify-center"
            >
              {mutation.isPending ? '...' : t('social.post')}
            </Button>
          </div>
          {(isExpanded || content.length > 0) && (
            <div className="flex flex-col items-start mt-2 gap-1">
              <VisibilitySelect value={visibility} onChange={setVisibility} />
            </div>
          )}
        </form>
      </div>
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="bg-[#160C0C] border-2 border-white text-white w-[90vw] max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-white font-sans text-lg">Create Post</DialogTitle>
            <DialogDescription className="text-gray-300 text-sm">
              Share your thoughts with the community
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-gray-300 mb-4 text-base">
              Sign in to interact with thousands of jiu-jitsu practitioners and join the conversation around the world!
            </p>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowAuthModal(false);
                  navigate('/');
                }}
                className="bg-sossiety-red-600 hover:bg-sossiety-red-700 text-white px-6"
              >
                Sign in
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
